import { CircularProgress } from '@material-ui/core'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { useHistory } from 'react-router-dom'
import {
  useAbundanceEngineGetViewPathDetails,
  useDuplicateAbundanceEngineView,
  useListViewTags
} from '../../../../../api/abundanceEngine'
import useDebouncedValue from '../../../../../hooks/useDebounceValue'
import Alert from '../../../../atoms/Alert'
import SydAutocomplete from '../../../../commonDesign/SydAutocomplete'
import SydButton from '../../../../commonDesign/Button'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import ViewDetailInputs from '../AbundanceEngineEditView/ViewDetailInputs'

const getSourceTagOptions = (sourceView) => {
  return [...new Set((sourceView?.tags || [])
    .map((tag) => tag.viewTagId ?? tag.tagId)
    .filter(tagId => tagId != null))]
}

const defaultFormValues = {
  viewId: null,
  path: '',
  name: '',
  description: '',
  isLive: false,
  showInNavigation: false,
  hideTitle: false,
  disabledViewId: null,
  enablementCodeId: null,
  liveViewVersionId: null,
  viewTagIds: []
}

function DuplicateViewModal ({
  open,
  onClose,
  sourceView
}) {
  const history = useHistory()
  const [submitError, setSubmitError] = useState(null)
  const [searchText, setSearchText] = useState('')
  const debouncedSearchText = useDebouncedValue(searchText, 200)

  const form = useForm({ defaultValues: defaultFormValues })

  const { data: sourceViewDetails, isFetching: isFetchingSourceViewDetails } = useAbundanceEngineGetViewPathDetails(
    { viewId: sourceView?.viewId },
    { enabled: open && !!sourceView?.viewId }
  )

  const { data: viewTags = [], isLoading: isLoadingViewTags } = useListViewTags(
    {
      sort: [{ field: 'name', dir: 'asc' }],
      filters: debouncedSearchText
        ? {
          search: [{ op: 'contains', value: debouncedSearchText }]
        }
        : undefined
    },
    { enabled: open }
  )

  const tagOptions = useMemo(
    () => (viewTags || []).map((tag) => ({
      label: tag.displayName || tag.name,
      name: tag.name,
      subLabel: tag.description,
      value: tag.viewTagId
    })),
    [viewTags]
  )

  useEffect(() => {
    if (open) return
    form.reset(defaultFormValues)
    setSubmitError(null)
    setSearchText('')
  }, [open, form])

  useEffect(() => {
    if (!open || !sourceView?.viewId) return
    if (form.formState.isDirty) return
    const defaults = sourceViewDetails || sourceView

    form.reset({
      viewId: null,
      path: '',
      name: defaults?.name || '',
      description: defaults?.description || '',
      isLive: !!defaults?.isLive,
      showInNavigation: !!defaults?.showInNavigation,
      hideTitle: !!defaults?.hideTitle,
      disabledViewId: defaults?.disabledViewId ?? null,
      enablementCodeId: defaults?.enablementCodeId ?? null,
      liveViewVersionId: null,
      viewTagIds: getSourceTagOptions(sourceView)
    })
    setSubmitError(null)
    setSearchText('')
  }, [open, sourceView, sourceView?.viewId, sourceViewDetails, form, form.formState.isDirty])

  const duplicateView = useDuplicateAbundanceEngineView()

  const handleDuplicate = form.handleSubmit(async (formData) => {
    setSubmitError(null)
    try {
      const path = formData.path?.trim()
      const name = formData.name?.trim()

      const duplicatedView = await duplicateView.mutateAsync({
        sourceViewId: sourceView?.viewId,
        path,
        name,
        description: formData.description?.trim() || null,
        isLive: !!formData.isLive,
        showInNavigation: !!formData.showInNavigation,
        hideTitle: !!formData.hideTitle,
        disabledViewId: formData.disabledViewId ?? null,
        enablementCodeId: formData.enablementCodeId ?? null,
        viewTagIds: formData.viewTagIds || []
      })

      const nextPath = duplicatedView?.path || duplicatedView?.view?.path || path
      onClose?.()
      history.push(`/admin/abundance-engine-views/edit/${nextPath}`)
    } catch (err) {
      console.error(err)
      setSubmitError(err?.message || 'Failed to duplicate view')
    }
  })

  const isLoadingDetails = open && !!sourceView?.viewId && isFetchingSourceViewDetails && !sourceViewDetails

  return (
    <SydModal
      disablePortal={false}
      open={open}
      onClose={onClose}
      title='Duplicate View'
      subtitle='Create one new view from an existing view and adjust its settings.'
      size='large'
    >
      <form onSubmit={handleDuplicate} className='flex flex-col gap-md' style={{ maxHeight: '70vh' }}>
        <FormProvider {...form}>
          <div className='flex-1' style={{ overflow: 'auto' }}>
            {isLoadingDetails ? (
              <div className='flex items-center justify-center p-4'>
                <CircularProgress size={20} />
              </div>
            ) : (
              <>
                {submitError && (
                  <Alert severity='error'>
                    {submitError}
                  </Alert>
                )}

                <ViewDetailInputs />

                <Controller
                  name='viewTagIds'
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <SydLabel
                      label='Tags'
                      description='Assign existing tags to the duplicated view.'
                      error={hookFormErrorAdapter(form, fieldState)}
                    >
                      <SydAutocomplete
                        multiple
                        options={tagOptions}
                        placeholder='Select tag(s)'
                        isLoading={isLoadingViewTags}
                        onInputChange={(_, value) => setSearchText(value)}
                        value={tagOptions.filter(tag => (field.value || []).includes(tag.value))}
                        onChange={(_, value) => field.onChange((value || []).map(tag => tag.value))}
                      />
                    </SydLabel>
                  )}
                />
              </>
            )}
          </div>

          <SydModalActions className='w-full'>
            <SydButton
              type='button'
              variant='outline'
              onClick={onClose}
              disabled={duplicateView.isPending}
            >
              Cancel
            </SydButton>
            <SydButton
              type='submit'
              variant='primary'
              processing={duplicateView.isPending}
              disabled={duplicateView.isPending || isLoadingDetails}
            >
              Duplicate View
            </SydButton>
          </SydModalActions>
        </FormProvider>
      </form>
    </SydModal>
  )
}

DuplicateViewModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  sourceView: PropTypes.shape({
    viewId: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    isLive: PropTypes.bool,
    showInNavigation: PropTypes.bool,
    hideTitle: PropTypes.bool,
    disabledViewId: PropTypes.number,
    enablementCodeId: PropTypes.number,
    tags: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string,
        displayName: PropTypes.string,
        tagId: PropTypes.number,
        viewTagId: PropTypes.number
      })
    )
  })
}

export default DuplicateViewModal
